/**
 * Sidebar v2 custom snooze dialog: date+time or relative duration, resolved
 * through the shared snooze rules.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/CustomSnoozeDialog.tsx`
 * at pin e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: forge ui primitives (dialog, popover,
 * toggle-group, select) plus the already-vendored calendar and number-field
 * components; forge has no week-start setting, so the calendar keeps its
 * library default.
 */
import { CalendarIcon } from "lucide-react";
import { useEffect, useId, useState, type FormEvent } from "react";
import { create } from "zustand";
import { resolveCustomSnooze, type CustomSnoozeInput } from "./sidebarV2Settled";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Toggle, ToggleGroup } from "~/components/ui/toggle-group";
import { Calendar } from "~/vendor/upstream/components/ui/calendar";
import {
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
} from "~/vendor/upstream/components/ui/number-field";

type SnoozeChoice = { readonly snoozedUntil: string };
type Request = { readonly resolve: (choice: SnoozeChoice | null) => void };
const useRequest = create<{ request: Request | null }>(() => ({ request: null }));

/** Open a snooze request and resolve it with a choice or null; cancel any previous pending request. */
export function requestCustomSnooze(): Promise<SnoozeChoice | null> {
  useRequest.getState().request?.resolve(null);
  return new Promise((resolve) => useRequest.setState({ request: { resolve } }));
}

/** Clear the pending dialog request before resolving it with the selected wake time or cancellation. */
function finish(choice: SnoozeChoice | null) {
  const request = useRequest.getState().request;
  useRequest.setState({ request: null });
  request?.resolve(choice);
}

/** Format the local calendar date as YYYY-MM-DD without shifting it to the UTC date. */
function localSnoozeDate(date: Date): string {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
}

/** Format local hours and minutes as a zero-padded HH:mm value for the time input. */
function localSnoozeTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}
void localSnoozeDate;
void localSnoozeTime;

/** Mount the dialog for a pending request and resolve that request as cancelled on unmount. */
export function CustomSnoozeDialogHost() {
  const request = useRequest((state) => state.request);
  useEffect(() => () => finish(null), []);
  return request ? <CustomSnoozeDialog /> : null;
}

/** Collect a local date/time or relative duration and resolve the pending snooze request after validation. */
function CustomSnoozeDialog() {
  const id = useId();
  const [initial] = useState(() => new Date(Date.now() + 3_600_000));
  const [mode, setMode] = useState<CustomSnoozeInput["mode"]>("date");
  const [date, setDate] = useState(initial);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [time, setTime] = useState(localSnoozeTime(initial));
  const [amount, setAmount] = useState("2");
  const [unit, setUnit] = useState<"minutes" | "hours" | "days">("hours");
  const [error, setError] = useState<string | null>(null);
  const input: CustomSnoozeInput =
    mode === "date" ? { mode, date: localSnoozeDate(date), time } : { mode, amount, unit };

  /** Validate the requested wake time, showing an error or completing the snooze request. */
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const snoozedUntil = resolveCustomSnooze(input, new Date());
    if (!snoozedUntil) {
      setError(
        mode === "date"
          ? "Choose a valid date and time in the future."
          : "Enter a positive duration.",
      );
      return;
    }
    finish({ snoozedUntil });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) finish(null);
      }}
    >
      <DialogPopup className="sm:max-w-sm">
        <form className="flex min-h-0 flex-col" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Custom snooze</DialogTitle>
            <DialogDescription>Choose when snoozed threads return to your inbox.</DialogDescription>
          </DialogHeader>
          <DialogPanel>
            <div className="flex flex-col gap-4">
              <ToggleGroup
                aria-label="Schedule type"
                className="w-full *:flex-1"
                value={[mode]}
                onValueChange={(next: ReadonlyArray<string>) => {
                  const value = next[0];
                  if (value === "date" || value === "duration") setMode(value);
                  setError(null);
                }}
              >
                <Toggle value="date">Date and time</Toggle>
                <Toggle value="duration">Duration</Toggle>
              </ToggleGroup>
              {mode === "date" ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor={`${id}-date`}>Date</Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger
                        render={
                          <Button
                            id={`${id}-date`}
                            variant="outline"
                            className="w-full justify-between"
                          />
                        }
                      >
                        {date.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                        <CalendarIcon className="size-4 text-muted-foreground" />
                      </PopoverTrigger>
                      <PopoverPopup align="start" aria-label="Choose snooze date">
                        <Calendar
                          mode="single"
                          required
                          selected={date}
                          defaultMonth={date}
                          disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                          onSelect={(selected: Date | undefined) => {
                            if (!selected) return;
                            setDate(selected);
                            setCalendarOpen(false);
                            setError(null);
                          }}
                        />
                      </PopoverPopup>
                    </Popover>
                  </div>
                  <Label className="flex min-w-0 flex-col items-stretch" htmlFor={`${id}-time`}>
                    Time
                    <Input
                      nativeInput
                      id={`${id}-time`}
                      className="h-9 sm:h-8"
                      type="time"
                      required
                      value={time}
                      onChange={(event) => {
                        setTime(event.target.value);
                        setError(null);
                      }}
                    />
                  </Label>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <NumberField
                    id={`${id}-amount`}
                    min={0}
                    step="any"
                    value={amount === "" ? null : Number(amount)}
                    onValueChange={(value: number | null) => {
                      setAmount(value === null ? "" : String(value));
                      setError(null);
                    }}
                  >
                    <Label htmlFor={`${id}-amount`}>Snooze for</Label>
                    <NumberFieldGroup>
                      <NumberFieldDecrement aria-label="Decrease duration" />
                      <NumberFieldInput required />
                      <NumberFieldIncrement aria-label="Increase duration" />
                    </NumberFieldGroup>
                  </NumberField>
                  <Label className="flex min-w-0 flex-col items-stretch" htmlFor={`${id}-unit`}>
                    Unit
                    <Select
                      value={unit}
                      items={{ minutes: "Minutes", hours: "Hours", days: "Days" }}
                      onValueChange={(value: string | null) => {
                        if (value === "minutes" || value === "hours" || value === "days")
                          setUnit(value);
                        setError(null);
                      }}
                    >
                      <SelectTrigger id={`${id}-unit`} className="min-w-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectPopup>
                        <SelectItem value="minutes">Minutes</SelectItem>
                        <SelectItem value="hours">Hours</SelectItem>
                        <SelectItem value="days">Days</SelectItem>
                      </SelectPopup>
                    </Select>
                  </Label>
                </div>
              )}
            </div>
            {error ? (
              <p role="alert" className="text-destructive">
                {error}
              </p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => finish(null)}>
              Cancel
            </Button>
            <Button type="submit">Snooze</Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
